import {
    CompanyThresholdsMode,
    MonitorThresholdAlert,
    MonitorThresholdAlertMinimumLevel,
    MonitorThresholdLevelModel,
    ThresholdControlType,
    ThresholdEnableState,
    ThresholdInfo,
    ThresholdLevelActionType,
    ThresholdWorseningDirection
} from '../../types/thresholds';

export enum ThresholdValidationErrors {
    none = 0,
    companyMinimumNotMet = 1 << 1,
    progression = 1 << 2
}

export type ThresholdEffectiveLevelValue = {
    repeat: boolean;
    triggerOnImproving: boolean;
    value: number;
    unitIndex: number | null;
    actionType: ThresholdLevelActionType;
    actionTimeMinutes: number | null;
};

export type ThresholdLevelMergeResult = {
    isOverride: boolean;
    errors: ThresholdValidationErrors;
    value: ThresholdEffectiveLevelValue | null;
};

export abstract class ThresholdLevelMerger {
    static getForMode(mode: CompanyThresholdsMode): IThresholdLevelMerger {
        if (mode === CompanyThresholdsMode.notUsed || mode === CompanyThresholdsMode.specified) {
            return new EmptyMonitorMerger();
        } else if (mode === CompanyThresholdsMode.minimum) {
            return new MinimumMonitorMerger();
        } else if (mode === CompanyThresholdsMode.template) {
            return new TemplateMonitorMerger();
        }
        throw new Error('Unknown company mode');
    }
}

export interface IThresholdLevelMerger {
    mergeEnableStatus(
        user: ThresholdEnableState | null | undefined,
        company: ThresholdEnableState | null | undefined
    ): ThresholdEnableState;

    mergePriority(value: number | undefined | null, inherited: number | undefined | null): number | null;

    mergeAlertBooleans(
        value: MonitorThresholdAlert | null | undefined,
        inherited: MonitorThresholdAlert | null | undefined
    ): MonitorThresholdAlert;

    mergeAlertStrings(value: string | null | undefined, inherited: string | undefined | null): string | null;

    mergeLevels(
        value: MonitorThresholdLevelModel | null | undefined,
        inherited: MonitorThresholdLevelModel | null | undefined,
        info: ThresholdInfo
    ): ThresholdLevelMergeResult;

    get canIgnoreInheritedLevel(): boolean;
}

export class EmptyMonitorMerger implements IThresholdLevelMerger {
    get canIgnoreInheritedLevel(): boolean {
        return true;
    }

    mergeEnableStatus(value: ThresholdEnableState | null | undefined): ThresholdEnableState {
        return value ?? ThresholdEnableState.notSet;
    }

    mergePriority(value: number | undefined | null, inherited: number | undefined | null): number | null {
        return value ?? null;
    }

    mergeAlertBooleans(value: MonitorThresholdAlert | undefined, inherited: MonitorThresholdAlert | null): MonitorThresholdAlert {
        return {
            value: value?.value ?? false,
            minLevel: value?.minLevel ?? null
        };
    }

    mergeAlertStrings(value: string | null | undefined): string | null {
        return value ?? null;
    }

    mergeLevels(
        value: MonitorThresholdLevelModel | null | undefined,
        inherited: MonitorThresholdLevelModel | null | undefined,
        info: ThresholdInfo
    ): ThresholdLevelMergeResult {
        return {
            isOverride: false,
            value: value
                ? {
                      repeat: value.repeat ?? false,
                      triggerOnImproving: value.triggerOnImproving ?? false,
                      value: value.value ?? 0,
                      unitIndex: value.unitIndex ?? null,
                      actionType: value.actionType ?? ThresholdLevelActionType.none,
                      actionTimeMinutes: value.actionTimeMinutes ?? null
                  }
                : null,
            errors: ThresholdValidationErrors.none
        } satisfies ThresholdLevelMergeResult;
    }
}

export class TemplateMonitorMerger implements IThresholdLevelMerger {
    get canIgnoreInheritedLevel(): boolean {
        return true;
    }

    mergeEnableStatus(
        user: ThresholdEnableState | null | undefined,
        inherited: ThresholdEnableState | null | undefined
    ): ThresholdEnableState {
        if (user != null && user !== ThresholdEnableState.notSet) {
            return user;
        }
        if (inherited != null && inherited !== ThresholdEnableState.notSet) {
            return inherited;
        }

        return ThresholdEnableState.notSet;
    }

    mergePriority(value: number | undefined | null, inherited: number | undefined | null): number | null {
        return value ?? inherited ?? null;
    }

    mergeAlertBooleans(value: MonitorThresholdAlert | undefined, inherited: MonitorThresholdAlert | null): MonitorThresholdAlert {
        return {
            value: value?.value ?? inherited?.value ?? false,
            minLevel: value?.minLevel ?? inherited?.minLevel ?? null
        };
    }

    mergeAlertStrings(value: string | null | undefined, inherited: string | undefined | null): string | null {
        return value ?? inherited ?? null;
    }

    mergeLevels(
        value: MonitorThresholdLevelModel | null | undefined,
        inherited: MonitorThresholdLevelModel | null | undefined,
        info: ThresholdInfo
    ): ThresholdLevelMergeResult {
        if (value) {
            return {
                isOverride: !!inherited,
                errors: ThresholdValidationErrors.none,
                value: {
                    repeat: value.repeat ?? inherited?.repeat ?? false,
                    triggerOnImproving: value.triggerOnImproving ?? inherited?.triggerOnImproving ?? false,
                    value: value.value,
                    unitIndex: value.unitIndex,
                    actionType: value.actionType,
                    actionTimeMinutes: value.actionTimeMinutes
                }
            };
        }
        if (inherited) {
            return {
                isOverride: false,
                errors: ThresholdValidationErrors.none,

                value: {
                    repeat: inherited.repeat ?? false,
                    triggerOnImproving: inherited.triggerOnImproving ?? false,
                    value: inherited.value,
                    unitIndex: inherited.unitIndex,
                    actionType: inherited.actionType,
                    actionTimeMinutes: inherited.actionTimeMinutes
                }
            };
        }

        return {
            isOverride: false,
            errors: ThresholdValidationErrors.none,

            value: null
        };
    }
}

export class MinimumMonitorMerger implements IThresholdLevelMerger {
    get canIgnoreInheritedLevel(): boolean {
        return false;
    }

    mergeEnableStatus(
        user: ThresholdEnableState | null | undefined,
        company: ThresholdEnableState | null | undefined
    ): ThresholdEnableState {
        // Company minimum trumps everything
        if (company === ThresholdEnableState.enabled) {
            return ThresholdEnableState.enabled;
        }

        if (user === ThresholdEnableState.notSet) {
            return company ?? ThresholdEnableState.notSet;
        }

        return user ?? ThresholdEnableState.notSet;
    }

    mergePriority(value: number | undefined | null, inherited: number | undefined | null): number | null {
        const max = Math.max(value ?? -1, inherited ?? -1);
        if (max === -1) {
            return null;
        }
        return max;
    }

    mergeAlertBooleans(value: MonitorThresholdAlert | undefined, inherited: MonitorThresholdAlert | null): MonitorThresholdAlert {
        let minLevel: MonitorThresholdAlertMinimumLevel | null = null;
        if (value?.minLevel == null) {
            minLevel = inherited?.minLevel ?? null;
        } else if (inherited?.minLevel == null) {
            minLevel = value.minLevel;
        } else {
            minLevel = Math.min(value.minLevel, inherited.minLevel);
        }

        let enabled: boolean | null = null;
        if (value?.value == null) {
            enabled = inherited?.value ?? null;
        } else if (inherited?.value == null) {
            enabled = value.value;
        } else {
            enabled = value.value || inherited.value;
        }

        return {
            value: enabled,
            minLevel
        };
    }

    mergeAlertStrings(value: string | null | undefined, inherited: string | undefined | null): string | null {
        return (inherited || value) ?? null;
    }

    mergeLevels(
        value: MonitorThresholdLevelModel | null | undefined,
        inherited: MonitorThresholdLevelModel | null | undefined,
        info: ThresholdInfo
    ): ThresholdLevelMergeResult {
        if (value && inherited) {
            const worseValue =
                info.worseningDirection === ThresholdWorseningDirection.none
                    ? value
                    : compareLevels(value, inherited, info) > 0
                      ? value
                      : inherited;
            const actionTime =
                value.actionTimeMinutes === null
                    ? // If first value null use the inherited
                      inherited.actionTimeMinutes
                    : // If inherited null use first value
                      inherited.actionTimeMinutes === null
                      ? // If both not null use min
                        value.actionTimeMinutes
                      : Math.min(value.actionTimeMinutes, inherited.actionTimeMinutes);
            return {
                isOverride: !!inherited,
                value: {
                    repeat: value.repeat || inherited.repeat || false,
                    triggerOnImproving: value.triggerOnImproving || inherited.triggerOnImproving || false,
                    value: worseValue.value,
                    unitIndex: worseValue.unitIndex,
                    actionType: Math.max(value.actionType, inherited.actionType),
                    actionTimeMinutes: actionTime
                },
                errors: worseValue === value ? ThresholdValidationErrors.none : ThresholdValidationErrors.companyMinimumNotMet
            };
        }

        if (!value && !inherited) {
            return {
                isOverride: false,
                value: null,
                errors: ThresholdValidationErrors.none
            };
        }

        const nonNullValue = (value ?? inherited)!;

        return {
            isOverride: false,
            value: {
                repeat: nonNullValue.repeat ?? false,
                triggerOnImproving: nonNullValue.triggerOnImproving ?? false,
                value: nonNullValue.value ?? 0,
                unitIndex: nonNullValue.unitIndex ?? null,
                actionType: nonNullValue.actionType ?? ThresholdLevelActionType.none,
                actionTimeMinutes: nonNullValue.actionTimeMinutes ?? null
            },
            errors: ThresholdValidationErrors.none
        };
    }
}

const compareFuncs: Record<Exclude<ThresholdWorseningDirection, ThresholdWorseningDirection.none>, (a: number, b: number) => number> = {
    [ThresholdWorseningDirection.up]: (a, b) => a - b,
    [ThresholdWorseningDirection.down]: (a, b) => b - a
};

export function compareLevels(
    a: Pick<MonitorThresholdLevelModel, 'value' | 'unitIndex'> | null,
    b: Pick<MonitorThresholdLevelModel, 'value' | 'unitIndex'> | null,
    info: ThresholdInfo
): number {
    if (a === null && b === null) {
        return 0;
    }

    // a is > than null
    if (b === null) {
        return 1;
    }

    // null is < than b
    if (a === null) {
        return -1;
    }

    if (info.worseningDirection === ThresholdWorseningDirection.none) {
        return 0;
    }

    const compareFunc = compareFuncs[info.worseningDirection];
    if (info.controlType === ThresholdControlType.dropdown || info.controlType === ThresholdControlType.checkbox) {
        return compareFunc(a.value, b.value);
    }

    let commonUnitA = a.value;
    let commonUnitB = b.value;

    if (info.customUnits.length > 0) {
        commonUnitA = a.value * info.customUnits[a.unitIndex ?? 0].multiplier;
        commonUnitB = b.value * info.customUnits[b.unitIndex ?? 0].multiplier;
    }

    // Both not null
    return compareFunc(commonUnitA, commonUnitB);
}
